import { useQueryClient } from '@tanstack/react-query';
import {
  TEST_MANAGEMENT_CLOUD_TESTS,
  DESTINATIONS,
} from '@elementary/common-ui/constants/queryKeys';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { getCloudTestsQuery } from 'shared/services/api';
import { TestManagementCloudTest } from 'shared/types';

// If we don't need destinations, any cache will work
const findExistingCacheKey = (
  queryClient: ReturnType<typeof useQueryClient>,
  envid: string | undefined,
  includeAlertDestinations: boolean,
): (string | undefined)[] | null => {
  if (!includeAlertDestinations) {
    const withoutDestinationsKey = [TEST_MANAGEMENT_CLOUD_TESTS, envid];
    const withoutDestinationsData = queryClient.getQueryData(
      withoutDestinationsKey,
    );

    if (withoutDestinationsData) {
      return withoutDestinationsKey;
    }

    const withDestinationsKey = [
      TEST_MANAGEMENT_CLOUD_TESTS,
      envid,
      DESTINATIONS,
    ];
    const withDestinationsData = queryClient.getQueryData(withDestinationsKey);

    if (withDestinationsData) {
      return withDestinationsKey;
    }
  } else {
    const withDestinationsKey = [
      TEST_MANAGEMENT_CLOUD_TESTS,
      envid,
      DESTINATIONS,
    ];
    const withDestinationsData = queryClient.getQueryData(withDestinationsKey);

    if (withDestinationsData) {
      return withDestinationsKey;
    }
  }

  return null;
};

type Props = {
  includeAlertDestinations?: boolean;
  enabled?: boolean;
  onError?: () => void;
};

const useCloudTests = (
  { includeAlertDestinations = false, enabled = true, onError }: Props = {
    enabled: true,
  },
) => {
  const envid = useSelectedEnv();
  const queryClient = useQueryClient();

  const existingCacheKey = findExistingCacheKey(
    queryClient,
    envid,
    includeAlertDestinations,
  );

  const queryKey =
    existingCacheKey ||
    (includeAlertDestinations
      ? [TEST_MANAGEMENT_CLOUD_TESTS, envid, DESTINATIONS]
      : [TEST_MANAGEMENT_CLOUD_TESTS, envid]);

  return useGraphqlQuery<Array<TestManagementCloudTest>>({
    query: getCloudTestsQuery,
    variables: { includeAlertDestinations },
    queryKey,
    select: (val: { cloudTests: TestManagementCloudTest[] }) => val?.cloudTests,
    enabled: enabled,
    onError: onError,
  });
};

export default useCloudTests;
